import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { accountStore, setUserStore, userStore } from '@/services/auth/store';
import type { AugmentedUser } from '@/services/auth/types';
import { postUserFlags } from './api';

import type { UserFlags } from './api';
import { authKeys } from '@/services/auth/query-keys';

type setUserFlagsProps = {
  accountId: number | undefined;
  userId: number | undefined;
  flags: Partial<UserFlags>;
};

type SetUserFlagsOptions = {
  /** Runs at the mutation level, so it still fires when the caller unmounts mid-save. */
  onSuccess?: (props: setUserFlagsProps) => void;
};

export const useSetUserFlags = (options?: SetUserFlagsOptions) => {
  return useMutation({
    mutationFn: async (props: setUserFlagsProps) => postUserFlags(props.accountId, props.userId, props.flags),
    onSuccess: (_data, props) => options?.onSuccess?.(props),
  });
};

type AuthenticatedQueryData = { user: AugmentedUser | null } | null | undefined;

/**
 * Sets one of the signed-in user's flags, both on the server and on the user
 * the app already holds, so every `useAuth` reader sees the new value at once
 * rather than after the next sign-in.
 *
 * Does nothing when the held user already has that value. The check reads the
 * user at call time, not at render time, so a second call for the same flag
 * sends nothing even when it comes before the next render.
 */
export function useRecordUserFlag() {
  const queryClient = useQueryClient();
  const { mutate } = useSetUserFlags();

  return useCallback(
    (key: keyof UserFlags, value: boolean) => {
      const user = userStore.get();
      if (user == null || user.user_flags?.[key] === value) {
        return;
      }

      const userFlags = { ...user.user_flags, [key]: value };
      setUserStore({ ...user, user_flags: userFlags });
      queryClient.setQueryData<AuthenticatedQueryData>(authKeys.authenticated(), (current) =>
        current?.user == null ? current : { ...current, user: { ...current.user, user_flags: userFlags } },
      );
      mutate({ accountId: accountStore.get()?.id, userId: user.id, flags: { [key]: value } });
    },
    [queryClient, mutate],
  );
}
